import { create } from "zustand";
import { 
  URL_PARAM_PRESET
  // , URL_PARAM_PRESET_LEGACY
} from "../../../var";
import { filterPresetProps, getInitialPreset } from "./utils";
import {CropperPresetInterface} from './preset';
import { objectCompare } from "@src/v3.0.0/lib/utils";

export let globalHandler = null;
/** (app) cropper statuses */
export const CROPPER_STATUS_CHANGED = Symbol('changed');
export const CROPPER_STATUS_READY = Symbol('ready');
export const CROPPER_STATUS_NONE = Symbol('none');
export const CROPPER_MAX_HISTORY = 99;

interface CropperStoreDefinition {
  status: symbol;
  setStatus: (status: any) => void;
  setReady: () => void;
  setCropperReady: () => void;
  isCropperReady: () => void;
  setCropperChanged: () => void;
  setCropperNone: () => void;
  preset: CropperPresetInterface|object;
  undo: Array<CropperPresetInterface>;
  redo: Array<CropperPresetInterface>;
  undoPreset: any;
  redoPreset: any;
  element: HTMLImageElement|null;
  setElement: (element: HTMLImageElement) => void;
  getCropper: () => object|null;
  initPreset: (preset: CropperPresetInterface, zero: boolean) => void;
  updatePreset: (preset: CropperPresetInterface, store: boolean) => void;
  toJSON: any;
  toParams: any;
  isDark: boolean;
  setIsDark: any;
}

export const useCropperStore = create((set: any, get: any) : CropperStoreDefinition => ({
  status: CROPPER_STATUS_NONE,
  setStatus: (status: any) => set({ status }),
  setReady: () => set({ status: CROPPER_STATUS_READY }),
  setCropperReady: () => set({ status: CROPPER_STATUS_READY }),
  isCropperReady: () => get().status === CROPPER_STATUS_READY,
  setCropperChanged: () => set({ status: CROPPER_STATUS_CHANGED }),
  setCropperNone: () => set({ status: CROPPER_STATUS_NONE }),
  preset: {},
  undo: [],
  redo: [],
  async initPreset(preset: CropperPresetInterface, zero = false) {
    const initialPreset = getInitialPreset(JSON.parse(JSON.stringify(preset)), zero);
    await set({ preset: initialPreset, undo: [], redo: [] });
  },
  element: null,
  setElement(element: HTMLImageElement) {
    set({ element });
  },
  getCropper() {
    return get()?.element?.cropper;
  },
  isDark: false,
  setIsDark: (isDark: boolean) => set({ isDark }),
  updatePreset(preset: CropperPresetInterface, store = true) {
    // Confronta se il nuovo preset è diverso dal preset attuale
    const changedKeys = objectCompare(get().preset, { ...get().preset, ...preset });
    if (changedKeys.length > 0) {
      if (store) {
        // Comparazione con esclusione di chiavi
        // undo inseriti solo se non cambiano le chiavi escluse
        // eg. Se sono cambiate le chiavi escluse, non è un undo
        if(['media'].every(key => !changedKeys.includes(key))) {
          set((state: CropperStoreDefinition) => ({
            preset: { ...state.preset, ...preset },
            undo: [...state.undo, state.preset],
            redo: [],
          }));
        } else {
          set((state: CropperStoreDefinition) => ({
            preset: { ...state.preset, ...preset },
            undo: state.undo, // Non aggiungere nulla all'undo
            redo: [],
          }));
        }
      } else {
        const cropperJS = get()?.element?.cropper;
        if (cropperJS) {
          cropperJS.preset = { ...get().preset, ...preset };
        }
      }
    }
  },
  undoPreset: async () => {
    await set((state: CropperStoreDefinition) => {
      if (state.undo.length === 0) return state;
  
      const lastUndo = state.undo[state.undo.length - 1];
      return {
        preset: lastUndo,
        undo: state.undo.slice(0, -1),
        redo: [state.preset, ...state.redo],
      };
    });
    get().getCropper()._initFrame();
  },
  redoPreset: async () => {
    await set((state: CropperStoreDefinition) => {
      if (state.redo.length === 0) return state;
  
      const nextRedo = state.redo[0];
      return {
        preset: nextRedo,
        undo: [...state.undo, state.preset],
        redo: state.redo.slice(1),
      };
    });
    get().getCropper()._initFrame();
  },
  toJSON() {
    const cropper = get().element.cropper;
    const preset = cropper.preset;
    const {x, y} = cropper.getData();
    const filteredPreset = filterPresetProps({ ...preset, x, y });
    return filteredPreset;
  },
  toParams(encoded: boolean) {
    const jsonPreset = get().toJSON();
    const stringedPreset = JSON.stringify(jsonPreset);
    return {
      [URL_PARAM_PRESET]: encoded ? encodeURIComponent(stringedPreset) : stringedPreset
    };
  },
}));